/**
 * Payment Status Page
 *
 * Public page that handles Razorpay payment callbacks.
 * Shows detailed payment verification status with order details.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Loader2,
  ShoppingBag,
  CreditCard,
  ArrowRight,
  Phone,
  Mail,
  Package,
  Receipt,
  Shield,
  Truck,
  IndianRupee,
  BadgeCheck,
  AlertTriangle
} from 'lucide-react';

interface LineItem {
  title: string;
  variant_title: string | null;
  quantity: number;
  price: number;
  total: number;
  image_url: string | null;
  sku: string | null;
}

interface ShippingAddress {
  name: string;
  address1: string;
  address2: string | null;
  city: string;
  province: string;
  zip: string;
  country: string;
  phone: string | null;
}

interface OrderData {
  order_id: number;
  order_number: string;
  customer_name: string;
  email: string;
  phone: string;
  total_price: number;
  subtotal_price: number;
  total_discount: number;
  total_tax: number;
  currency: string;
  order_type: string;
  created_at: string;
  line_items: LineItem[];
  shipping_address: ShippingAddress | null;
}

interface PaymentData {
  payment_link_id: string;
  payment_id: string | null;
  amount_requested: number;
  amount_paid: number;
  remaining_balance: number;
  is_partial: boolean;
  payment_method: string | null;
  paid_at: string | null;
}

interface VerificationResult {
  success: boolean;
  data?: {
    status: 'paid' | 'partially_paid' | 'pending' | 'expired' | 'cancelled';
    order: OrderData;
    payment: PaymentData;
    redirect_url: string | null;
    admin_url: string | null;
  };
  message?: string;
  error?: string;
}

type PageState = 'loading' | 'success' | 'partial' | 'failed' | 'expired' | 'error';

export default function PaymentStatus() {
  const [searchParams] = useSearchParams();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [result, setResult] = useState<VerificationResult | null>(null);
  const [countdown, setCountdown] = useState(15);
  const [error, setError] = useState<string | null>(null);

  const paymentLinkId = searchParams.get('payment_link_id');
  const errorParam = searchParams.get('error');
  const errorMessage = searchParams.get('message');

  // Get logo and brand from env
  const logoUrl = import.meta.env.VITE_LOGIN_LOGO_URL || 'https://www.zeroharm.in/cdn/shop/files/Zeroharm_logo_1.png';
  const brandName = import.meta.env.VITE_BRAND_NAME || 'ZeroHarm';

  useEffect(() => {
    if (errorParam === 'true') {
      setPageState('error');
      setError(errorMessage || 'An error occurred during payment');
      return;
    }

    if (!paymentLinkId) {
      setPageState('error');
      setError('Invalid payment link');
      return;
    }

    verifyPayment();
  }, [paymentLinkId, errorParam]);

  const verifyPayment = async () => {
    try {
      setPageState('loading');

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8002/api';
      const response = await fetch(`${apiUrl}/razorpay/verify-payment/${paymentLinkId}`);
      const data: VerificationResult = await response.json();

      setResult(data);

      if (data.success && data.data) {
        switch (data.data.status) {
          case 'paid':
            setPageState('success');
            break;
          case 'partially_paid':
            setPageState('partial');
            break;
          case 'expired':
            setPageState('expired');
            break;
          case 'cancelled':
          case 'pending':
          default:
            setPageState('failed');
            break;
        }
      } else {
        setPageState('error');
        setError(data.message || 'Failed to verify payment');
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      setPageState('error');
      setError('Failed to verify payment. Please contact support.');
    }
  };

  // Countdown and redirect for successful payments
  useEffect(() => {
    if ((pageState === 'success' || pageState === 'partial') && result?.data?.redirect_url) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            window.location.href = result.data!.redirect_url!;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [pageState, result]);

  const handleRedirectNow = () => {
    if (result?.data?.redirect_url) {
      window.location.href = result.data.redirect_url;
    }
  };

  const formatCurrency = (amount: number, currency: string = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Asia/Kolkata'
    });
  };

  const getPaymentMethodLabel = (method: string | null) => {
    if (!method) return 'Online Payment';
    const labels: Record<string, string> = {
      upi: 'UPI',
      card: 'Credit/Debit Card',
      netbanking: 'Net Banking',
      wallet: 'Wallet',
      emi: 'EMI',
    };
    return labels[method] || method.charAt(0).toUpperCase() + method.slice(1);
  };

  const getPaymentMethodIcon = (method: string | null) => {
    switch (method) {
      case 'upi':
        return '📱';
      case 'card':
        return '💳';
      case 'netbanking':
        return '🏦';
      case 'wallet':
        return '👛';
      default:
        return '💰';
    }
  };

  // Render Order Items Section
  const renderOrderItems = (items: LineItem[]) => (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-100">
          {item.image_url ? (
            <img
              src={item.image_url}
              alt={item.title}
              className="w-16 h-16 object-cover rounded-lg bg-gray-100"
            />
          ) : (
            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-gray-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-gray-900 text-sm leading-tight">{item.title}</h4>
            {item.variant_title && (
              <p className="text-xs text-gray-500 mt-0.5">{item.variant_title}</p>
            )}
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-500">Qty: {item.quantity}</span>
              <span className="text-sm font-semibold text-gray-900">
                {formatCurrency(item.total)}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Render Shipping Address Section
  const renderShippingAddress = (address: ShippingAddress) => (
    <div className="p-4 bg-white rounded-xl border border-gray-100">
      <div className="flex items-center gap-2 text-gray-700 mb-2">
        <Truck className="w-4 h-4" />
        <span className="text-sm font-medium">Delivery Address</span>
      </div>
      <div className="text-sm text-gray-600 space-y-1">
        <p className="font-medium text-gray-900">{address.name}</p>
        <p>{address.address1}</p>
        {address.address2 && <p>{address.address2}</p>}
        <p>{address.city}, {address.province} {address.zip}</p>
        <p>{address.country}</p>
        {address.phone && (
          <p className="flex items-center gap-1 text-gray-500 mt-2">
            <Phone className="w-3 h-3" />
            {address.phone}
          </p>
        )}
      </div>
    </div>
  );

  // Render Price Breakdown Section
  const renderPriceBreakdown = (order: OrderData, payment: PaymentData) => (
    <div className="space-y-2 text-sm">
      <div className="flex justify-between text-gray-600">
        <span>Subtotal</span>
        <span>{formatCurrency(order.subtotal_price)}</span>
      </div>
      {order.total_discount > 0 && (
        <div className="flex justify-between text-green-600">
          <span>Discount</span>
          <span>-{formatCurrency(order.total_discount)}</span>
        </div>
      )}
      {order.total_tax > 0 && (
        <div className="flex justify-between text-gray-600">
          <span>Tax</span>
          <span>{formatCurrency(order.total_tax)}</span>
        </div>
      )}
      <div className="flex justify-between font-semibold text-gray-900 pt-2 border-t">
        <span>Order Total</span>
        <span>{formatCurrency(order.total_price)}</span>
      </div>
      <div className="flex justify-between text-green-600 font-medium pt-2">
        <span className="flex items-center gap-1">
          <CheckCircle className="w-4 h-4" />
          Amount Paid
        </span>
        <span>{formatCurrency(payment.amount_paid)}</span>
      </div>
      {payment.remaining_balance > 0 && (
        <div className="flex justify-between text-amber-600 font-medium">
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            Balance Due (COD)
          </span>
          <span>{formatCurrency(payment.remaining_balance)}</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <a href="https://zeroharm.in" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
            <img src={logoUrl} alt={brandName} className="h-8 object-contain" />
          </a>
          <div className="flex items-center gap-1.5 text-gray-500">
            <Shield className="w-4 h-4 text-green-600" />
            <span className="text-xs font-medium">Secure Payment</span>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 pb-8">
        {/* Loading State */}
        {pageState === 'loading' && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center mt-8">
            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment</h1>
            <p className="text-gray-500">Please wait while we confirm your payment with Razorpay...</p>
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-400">
              <Shield className="w-4 h-4" />
              <span>256-bit SSL Encrypted</span>
            </div>
          </div>
        )}

        {/* Success State */}
        {pageState === 'success' && result?.data && (
          <div className="space-y-4 mt-4">
            {/* Success Banner */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 text-center text-white shadow-lg">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-bold mb-1">Payment Successful!</h1>
              <p className="text-green-100 text-sm">Your order has been confirmed</p>
            </div>

            {/* Order Number Card */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="text-center">
                <span className="text-xs text-gray-500 uppercase tracking-wider">Order Number</span>
                <p className="text-2xl font-bold text-gray-900 mt-1">{result.data.order.order_number}</p>
                <p className="text-xs text-gray-400 mt-1">
                  Placed on {formatDate(result.data.order.created_at)}
                </p>
              </div>
            </div>

            {/* Payment Details Card */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-gray-700 mb-3">
                <CreditCard className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium">Payment Details</span>
                <BadgeCheck className="w-4 h-4 text-green-600 ml-auto" />
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{getPaymentMethodIcon(result.data.payment.payment_method)}</span>
                    <div>
                      <p className="font-medium text-gray-900">
                        {getPaymentMethodLabel(result.data.payment.payment_method)}
                      </p>
                      {result.data.payment.payment_id && (
                        <p className="text-xs text-gray-500">ID: {result.data.payment.payment_id}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-green-700">
                      {formatCurrency(result.data.payment.amount_paid)}
                    </p>
                    <p className="text-xs text-green-600">Paid</p>
                  </div>
                </div>
                {result.data.payment.paid_at && (
                  <p className="text-xs text-gray-500 text-center border-t border-green-200 pt-2 mt-2">
                    Paid on {formatDate(result.data.payment.paid_at)}
                  </p>
                )}
              </div>
            </div>

            {/* Order Items */}
            {(result.data.order.line_items?.length ?? 0) > 0 && (
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 text-gray-700 mb-3">
                  <ShoppingBag className="w-4 h-4" />
                  <span className="text-sm font-medium">Order Items ({result.data.order.line_items.length})</span>
                </div>
                {renderOrderItems(result.data.order.line_items)}
              </div>
            )}

            {/* Price Breakdown */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-gray-700 mb-3">
                <Receipt className="w-4 h-4" />
                <span className="text-sm font-medium">Payment Summary</span>
              </div>
              {renderPriceBreakdown(result.data.order, result.data.payment)}
            </div>

            {/* Shipping Address */}
            {result.data.order.shipping_address && (
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                {renderShippingAddress(result.data.order.shipping_address)}
              </div>
            )}

            {/* Customer Info */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-gray-700 mb-3">
                <Mail className="w-4 h-4" />
                <span className="text-sm font-medium">Contact Information</span>
              </div>
              <div className="space-y-2 text-sm">
                {result.data.order.customer_name && (
                  <p className="text-gray-900 font-medium">{result.data.order.customer_name}</p>
                )}
                {result.data.order.email && (
                  <p className="text-gray-600 flex items-center gap-2">
                    <Mail className="w-3 h-3" />
                    {result.data.order.email}
                  </p>
                )}
                {result.data.order.phone && (
                  <p className="text-gray-600 flex items-center gap-2">
                    <Phone className="w-3 h-3" />
                    {result.data.order.phone}
                  </p>
                )}
              </div>
            </div>

            {/* Redirect Notice */}
            {result.data.redirect_url && (
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
                <p className="text-sm text-gray-500 mb-4">
                  Redirecting to order confirmation in <span className="font-bold text-gray-900">{countdown}</span> seconds...
                </p>
                <button
                  onClick={handleRedirectNow}
                  className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-colors"
                >
                  View Order on Store
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Partial Payment State */}
        {pageState === 'partial' && result?.data && (
          <div className="space-y-4 mt-4">
            {/* Partial Payment Banner */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 text-center text-white shadow-lg">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-bold mb-1">Partial Payment Received</h1>
              <p className="text-amber-100 text-sm">Pay remaining balance on delivery</p>
            </div>

            {/* Order Number Card */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="text-center">
                <span className="text-xs text-gray-500 uppercase tracking-wider">Order Number</span>
                <p className="text-2xl font-bold text-gray-900 mt-1">{result.data.order.order_number}</p>
              </div>
            </div>

            {/* Payment Status Card */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-gray-700 mb-3">
                <IndianRupee className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium">Payment Breakdown</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                  <p className="text-xs text-green-600 uppercase">Paid Online</p>
                  <p className="text-xl font-bold text-green-700">
                    {formatCurrency(result.data.payment.amount_paid)}
                  </p>
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    {getPaymentMethodLabel(result.data.payment.payment_method)}
                  </p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                  <p className="text-xs text-amber-600 uppercase">Pay on Delivery</p>
                  <p className="text-xl font-bold text-amber-700">
                    {formatCurrency(result.data.payment.remaining_balance)}
                  </p>
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <Truck className="w-3 h-3" />
                    Cash/UPI
                  </p>
                </div>
              </div>
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Order Total</span>
                  <span className="text-lg font-bold text-gray-900">
                    {formatCurrency(result.data.order.total_price)}
                  </span>
                </div>
              </div>
            </div>

            {/* Order Items */}
            {(result.data.order.line_items?.length ?? 0) > 0 && (
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 text-gray-700 mb-3">
                  <ShoppingBag className="w-4 h-4" />
                  <span className="text-sm font-medium">Order Items ({result.data.order.line_items.length})</span>
                </div>
                {renderOrderItems(result.data.order.line_items)}
              </div>
            )}

            {/* Shipping Address */}
            {result.data.order.shipping_address && (
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                {renderShippingAddress(result.data.order.shipping_address)}
              </div>
            )}

            {/* Transaction ID */}
            {result.data.payment.payment_id && (
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 text-gray-700 mb-2">
                  <Receipt className="w-4 h-4" />
                  <span className="text-sm font-medium">Transaction Details</span>
                </div>
                <div className="text-xs text-gray-500 space-y-1">
                  <p>Payment ID: <span className="font-mono text-gray-700">{result.data.payment.payment_id}</span></p>
                  <p>Link ID: <span className="font-mono text-gray-700">{result.data.payment.payment_link_id}</span></p>
                </div>
              </div>
            )}

            {/* Redirect Notice */}
            {result.data.redirect_url && (
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
                <p className="text-sm text-gray-500 mb-4">
                  Redirecting in <span className="font-bold text-gray-900">{countdown}</span> seconds...
                </p>
                <button
                  onClick={handleRedirectNow}
                  className="w-full py-3 px-4 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-colors"
                >
                  View Order Details
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Failed State */}
        {pageState === 'failed' && (
          <div className="space-y-4 mt-8">
            <div className="bg-gradient-to-r from-red-500 to-rose-600 rounded-2xl p-6 text-center text-white shadow-lg">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-bold mb-1">Payment Failed</h1>
              <p className="text-red-100 text-sm">Your payment could not be processed</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h3 className="font-medium text-gray-900 mb-2">Don't worry!</h3>
              <p className="text-gray-600 text-sm mb-4">
                No amount has been deducted from your account. If any amount was debited,
                it will be refunded within 5-7 business days.
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => window.history.back()}
                  className="w-full py-3 px-4 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-xl transition-colors"
                >
                  Try Again
                </button>
                <p className="text-xs text-gray-400 mt-4">
                  Need help? Contact support with reference ID if available
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Expired State */}
        {pageState === 'expired' && (
          <div className="space-y-4 mt-8">
            <div className="bg-gradient-to-r from-gray-500 to-gray-600 rounded-2xl p-6 text-center text-white shadow-lg">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-bold mb-1">Payment Link Expired</h1>
              <p className="text-gray-200 text-sm">This payment link is no longer valid</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
              <p className="text-gray-600 mb-6">
                Payment links are valid for a limited time. Please request a new payment link to complete your purchase.
              </p>
              <button
                onClick={() => window.history.back()}
                className="w-full py-3 px-4 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-xl transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        )}

        {/* Error State */}
        {pageState === 'error' && (
          <div className="space-y-4 mt-8">
            <div className="bg-gradient-to-r from-red-500 to-rose-600 rounded-2xl p-6 text-center text-white shadow-lg">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-bold mb-1">Something Went Wrong</h1>
              <p className="text-red-100 text-sm">We couldn't verify your payment</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
              <p className="text-gray-600 mb-2">
                {error || 'An unexpected error occurred. Please try again.'}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                If you believe the payment was successful, please contact support with your payment reference.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="w-full py-3 px-4 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-xl transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8 space-y-2">
          <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <Shield className="w-3 h-3" />
              Secure
            </span>
            <span>•</span>
            <span>Powered by Razorpay</span>
            <span>•</span>
            <span>{brandName}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
